import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type FocusEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import styled, { css, keyframes, type DefaultTheme } from "styled-components";

import { Text, defaultTextVariants } from "../Text";
import { IconButton } from "../IconButton";
import { overlayZIndex } from "../foundations/popover";
import { controlFocusRing, controlTransition } from "../foundations/controlStyles";
import {
  CheckCircleFilledIcon,
  CloseCircleFilledIcon,
  CloseIcon,
  InfoFilledIcon,
  WarningFilledIcon,
  type IconProps,
} from "../../icons/glyphs";
import { prefersReducedMotion } from "../../utils/prefersReducedMotion";
import {
  toast as defaultManager,
  type Toast,
  type ToastManager,
  type ToastTone,
} from "./ToastManager";

/** 9-position 배치 기준점. 컨테이너를 뷰포트 모서리/변에 고정한다. */
export type ToastGravity =
  | "top-left"
  | "top-center"
  | "top-right"
  | "left"
  | "center"
  | "right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

/**
 * tone 표현 방식. resting 외관 보존을 위해 기본은 `"stripe"`(현행).
 * - `stripe`: 좌측 3px tone 색 띠만.
 * - `icon`: 메시지 앞 tone 색 아이콘, 좌측 띠 없음(sonner/Carbon 류).
 * - `icon-stripe`: 아이콘 + 좌측 띠(가장 강조).
 * - `tinted`: 좌측 띠 + 면에 옅은 tone 틴트.
 */
export type ToastVariant = "stripe" | "icon" | "icon-stripe" | "tinted";

/**
 * enter/exit 모션 프리셋(host-wide). 기본은 `"subtle"`(resting 외관 보존).
 * - `subtle`: 제자리에서 gravity 방향 12px 슬라이드 + fade.
 * - `slide`: 가장 가까운 뷰포트 변 바깥에서 직선으로 들어오고 같은 길로 나간다.
 * - `bounce`: `slide` 경로에 오버슈트 — 정착 전 반대쪽으로 살짝 넘어갔다 돌아온다. 주변 시야에서
 *   알림이 감지돼야 하는 작업 화면(시선이 중앙에 있는 앱)용.
 * - `zoom`: 이동 없이 scale 축소 상태에서 확대 등장, 축소 퇴장.
 * `prefers-reduced-motion: reduce`면 프리셋과 무관하게 모션을 생략한다.
 */
export type ToastMotion = "subtle" | "slide" | "bounce" | "zoom";

export interface ToastHostProps {
  // 테스트·격리를 위해 주입 가능. 기본은 패키지 스코프 싱글턴.
  manager?: ToastManager;
  // 스택을 띄울 위치. 기본 우상단.
  gravity?: ToastGravity;
  // tone 표현 방식. 기본 좌측 색 띠.
  variant?: ToastVariant;
  // enter/exit 모션 프리셋. 기본 제자리 12px 슬라이드 + fade.
  motion?: ToastMotion;
  // 포인터 hover 또는 키보드 focus가 스택 안에 있는 동안 자동 닫힘을 멈출지. 기본 true(스택 전체 멈춤).
  pauseOnHover?: boolean;
}

// gravity별 뷰포트 anchor + 스택 정렬. 세로 중앙/가로 중앙은 transform으로 보정한다.
const GRAVITY_STYLES: Record<ToastGravity, ReturnType<typeof css>> = {
  "top-left": css`
    top: 1rem;
    left: 1rem;
    align-items: flex-start;
  `,
  "top-center": css`
    top: 1rem;
    left: 50%;
    transform: translateX(-50%);
    align-items: center;
  `,
  "top-right": css`
    top: 1rem;
    right: 1rem;
    align-items: flex-end;
  `,
  left: css`
    top: 50%;
    left: 1rem;
    transform: translateY(-50%);
    align-items: flex-start;
  `,
  center: css`
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    align-items: center;
  `,
  right: css`
    top: 50%;
    right: 1rem;
    transform: translateY(-50%);
    align-items: flex-end;
  `,
  "bottom-left": css`
    bottom: 1rem;
    left: 1rem;
    align-items: flex-start;
  `,
  "bottom-center": css`
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    align-items: center;
  `,
  "bottom-right": css`
    bottom: 1rem;
    right: 1rem;
    align-items: flex-end;
  `,
};

// 시작 오프셋(tx/ty)과 진입 방향 단위(dx/dy ∈ -1|0|1). 값은 keyframe 안 calc()에 들어가므로 0도 반드시
// 단위를 붙인다 — 단위 없는 0은 calc 곱셈 결과가 <number>가 돼 translate() 선언 전체가 무효가 된다.
type SlideOffset = { tx: string; ty: string; dx: -1 | 0 | 1; dy: -1 | 0 | 1 };

// subtle: 카드가 제자리에서 살짝 밀려 들어오는 방향(가장 가까운 모서리에서). 중앙은 살짝 위에서.
const SUBTLE_OFFSET: Record<ToastGravity, SlideOffset> = {
  "top-left": { tx: "0px", ty: "-12px", dx: 0, dy: -1 },
  "top-center": { tx: "0px", ty: "-12px", dx: 0, dy: -1 },
  "top-right": { tx: "0px", ty: "-12px", dx: 0, dy: -1 },
  left: { tx: "-12px", ty: "0px", dx: -1, dy: 0 },
  center: { tx: "0px", ty: "-8px", dx: 0, dy: -1 },
  right: { tx: "12px", ty: "0px", dx: 1, dy: 0 },
  "bottom-left": { tx: "0px", ty: "12px", dx: 0, dy: 1 },
  "bottom-center": { tx: "0px", ty: "12px", dx: 0, dy: 1 },
  "bottom-right": { tx: "0px", ty: "12px", dx: 0, dy: 1 },
};

// slide/bounce: 가장 가까운 뷰포트 변 바깥에서 들어온다. 좌우 열은 옆 변에서 카드 폭 + 호스트 inset만큼
// (열의 x가 같아 스택 순서와 무관). 상하 중앙과 center는 세로 진입인데, 자기 높이 기준(`-100%`)이면 두 번째
// 카드부터 앞 카드 위에 겹친 채 등장하므로 뷰포트 높이(`±100vh`) 기준으로 스택 어디서든 확실히 바깥에서
// 출발한다 — 늘어난 거리는 ease-out과 bounce의 첫 구간이 흡수한다. 호스트가 fixed라 밖에 있어도 문서
// 스크롤이 생기지 않는다.
const OFFSCREEN_OFFSET: Record<ToastGravity, SlideOffset> = {
  "top-left": { tx: "calc(-100% - 1rem)", ty: "0px", dx: -1, dy: 0 },
  "top-center": { tx: "0px", ty: "-100vh", dx: 0, dy: -1 },
  "top-right": { tx: "calc(100% + 1rem)", ty: "0px", dx: 1, dy: 0 },
  left: { tx: "calc(-100% - 1rem)", ty: "0px", dx: -1, dy: 0 },
  center: { tx: "0px", ty: "-100vh", dx: 0, dy: -1 },
  right: { tx: "calc(100% + 1rem)", ty: "0px", dx: 1, dy: 0 },
  "bottom-left": { tx: "calc(-100% - 1rem)", ty: "0px", dx: -1, dy: 0 },
  "bottom-center": { tx: "0px", ty: "100vh", dx: 0, dy: 1 },
  "bottom-right": { tx: "calc(100% + 1rem)", ty: "0px", dx: 1, dy: 0 },
};

const NO_OFFSET: SlideOffset = { tx: "0px", ty: "0px", dx: 0, dy: 0 };

// tinted error/warning은 status 어휘 대신 red 리저브 알림 면(dangerSurface)으로 렌더한다 —
// 팔레트 시트 §Semantic Reserve · Red의 알림 카드 매핑(RFC-toast-primitive §tone 매핑). info/success와
// stripe/icon 계열 variant는 기존 status.* 매핑을 유지한다.
const isDangerSurface = (tone: ToastTone, variant: ToastVariant): boolean =>
  variant === "tinted" && (tone === "error" || tone === "warning");

// 좌측 색 띠·아이콘 색. status 토큰은 light/dark 모두 정의됨.
const toneColor = (theme: DefaultTheme, tone: ToastTone): string => {
  switch (tone) {
    case "info":
      return theme.color.status.info;
    case "success":
      return theme.color.status.success;
    case "warning":
      return theme.color.status.warning;
    case "error":
      return theme.color.status.error;
  }
};

// subtle/slide 공용 — 시작 오프셋만 다르다(`--c6-toast-tx/ty`는 호스트가 gravity·프리셋으로 정한다).
const slideEnter = keyframes`
  from {
    opacity: 0;
    transform: translate(var(--c6-toast-tx, 0px), var(--c6-toast-ty, 0px));
  }
  to {
    opacity: 1;
    transform: translate(0px, 0px);
  }
`;

const slideExit = keyframes`
  from {
    opacity: 1;
    transform: translate(0px, 0px);
  }
  to {
    opacity: 0;
    transform: translate(var(--c6-toast-tx, 0px), var(--c6-toast-ty, 0px));
  }
`;

// bounce: 들어온 방향 반대로 24px 넘어갔다 10px·4px로 잦아드는 세 번의 오버슈트. 진입 방향 단위
// (`--c6-toast-dx/dy`)에 절대 px를 곱한다 — 이동 거리가 방향·gravity마다 달라도(가로는 카드 폭, 세로는
// 뷰포트 높이) 튐은 같아야 한다. 나갈 땐 반대쪽으로 16px 당겼다(anticipation) 튀어나간다.
const bounceEnter = keyframes`
  0% {
    opacity: 0;
    transform: translate(var(--c6-toast-tx, 0px), var(--c6-toast-ty, 0px));
  }
  45% {
    opacity: 1;
    transform: translate(calc(var(--c6-toast-dx, 0) * -24px), calc(var(--c6-toast-dy, 0) * -24px));
  }
  68% {
    transform: translate(calc(var(--c6-toast-dx, 0) * 10px), calc(var(--c6-toast-dy, 0) * 10px));
  }
  86% {
    transform: translate(calc(var(--c6-toast-dx, 0) * -4px), calc(var(--c6-toast-dy, 0) * -4px));
  }
  100% {
    transform: translate(0px, 0px);
  }
`;

const bounceExit = keyframes`
  0% {
    opacity: 1;
    transform: translate(0px, 0px);
  }
  35% {
    opacity: 1;
    transform: translate(calc(var(--c6-toast-dx, 0) * -16px), calc(var(--c6-toast-dy, 0) * -16px));
  }
  100% {
    opacity: 0;
    transform: translate(var(--c6-toast-tx, 0px), var(--c6-toast-ty, 0px));
  }
`;

const zoomEnter = keyframes`
  from {
    opacity: 0;
    transform: scale(0.85);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const zoomExit = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.85);
  }
`;

type MotionPreset = {
  enter: ReturnType<typeof keyframes>;
  exit: ReturnType<typeof keyframes>;
  enterMs: number;
  // exit 길이는 presence 버퍼가 사라진 토스트를 mount해 두는 시간이기도 하다.
  exitMs: number;
  enterEasing: string;
  exitEasing: string;
  offset: Record<ToastGravity, SlideOffset> | null;
};

// 감속(ease-out expo)으로 들어오고 가속(ease-in)으로 나간다 — 등장은 눈에 잡히게, 퇴장은 빨리 비키게.
// slide는 이 곡선으로 제자리에 "딱" 멈추고, bounce는 같은 경로에 오버슈트 keyframe을 얹어 갈라진다.
const EASE_OUT = "cubic-bezier(0.16, 1, 0.3, 1)";
const EASE_IN = "cubic-bezier(0.64, 0, 0.78, 0)";
// bounce keyframe 구간마다 적용되는 감속(animate.css bounceIn 계열과 같은 곡선).
const EASE_BOUNCE = "cubic-bezier(0.215, 0.61, 0.355, 1)";

const MOTION: Record<ToastMotion, MotionPreset> = {
  subtle: {
    enter: slideEnter,
    exit: slideExit,
    enterMs: 220,
    exitMs: 220,
    enterEasing: "ease",
    exitEasing: "ease",
    offset: SUBTLE_OFFSET,
  },
  slide: {
    enter: slideEnter,
    exit: slideExit,
    enterMs: 260,
    exitMs: 200,
    enterEasing: EASE_OUT,
    exitEasing: EASE_IN,
    offset: OFFSCREEN_OFFSET,
  },
  bounce: {
    enter: bounceEnter,
    exit: bounceExit,
    enterMs: 640,
    exitMs: 420,
    enterEasing: EASE_BOUNCE,
    exitEasing: EASE_BOUNCE,
    offset: OFFSCREEN_OFFSET,
  },
  zoom: {
    enter: zoomEnter,
    exit: zoomExit,
    enterMs: 200,
    exitMs: 160,
    enterEasing: EASE_OUT,
    exitEasing: EASE_IN,
    offset: null,
  },
};

// 같은 카드가 replace될 때의 갱신 큐. 카드는 그대로 두고 바뀐 내용만 교체되는 것을 보여 준다 —
// 새 문구·아이콘이 아래에서 살짝 떠오르며 들어오고, tone 색 빛이 띠 쪽에서 카드를 한 번 훑고 지나간다.
// 새 알림의 enter(카드 전체 이동)와 형태가 달라 "새 토스트"와 "토스트가 갱신됨"이 구분된다.
const contentSwap = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.35rem);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;
const CONTENT_SWAP_MS = 280;

const sheenSweep = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(280%);
  }
`;
const SHEEN_MS = 720;

// 갱신 시 내용 슬롯에 붙는 등장 모션. key로 revision마다 재시작한다.
const contentSwapCss = css`
  animation: ${contentSwap} ${CONTENT_SWAP_MS}ms ${EASE_OUT} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Sheen = styled.span<{ $tone: ToastTone }>`
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: inherit;
  pointer-events: none;
  color: ${({ theme, $tone }) => toneColor(theme, $tone)};
  /* 밝은 면에서는 tone 색 그대로가 잘 읽힌다. */
  --c6-toast-sheen: color-mix(in srgb, currentColor 14%, transparent);

  /* 어두운 면에서는 tone 색이 면 밝기에 묻히므로 잉크를 흰색 쪽으로 끌어올리고 세기를 높인다. */
  :root[data-c6-theme="dark"] & {
    --c6-toast-sheen: color-mix(
      in srgb,
      color-mix(in srgb, currentColor 55%, white) 26%,
      transparent
    );
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 60%;
    background: linear-gradient(
      100deg,
      transparent 0%,
      var(--c6-toast-sheen) 50%,
      transparent 100%
    );
    animation: ${sheenSweep} ${SHEEN_MS}ms ease-in-out both;
  }

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const Host = styled.div<{ $gravity: ToastGravity }>`
  position: fixed;
  z-index: ${overlayZIndex.toast};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: max-content;
  max-width: min(28rem, calc(100vw - 2rem));
  /* 호스트의 빈 공간은 클릭이 통과하고, 개별 토스트만 입력을 받는다. */
  pointer-events: none;
  ${({ $gravity }) => GRAVITY_STYLES[$gravity]}
`;

const Card = styled.div<{ $tone: ToastTone; $variant: ToastVariant; $motion: ToastMotion }>`
  position: relative;
  display: flex;
  max-width: 100%;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.color.border.default};
  border-left-width: ${({ $variant }) => ($variant === "icon" ? "1px" : "3px")};
  border-left-color: ${({ theme, $tone, $variant }) =>
    $variant === "icon" ? theme.color.border.default : toneColor(theme, $tone)};
  border-radius: ${({ theme }) => theme.shape.radius.surface};
  background-color: ${({ theme, $tone, $variant }) =>
    $variant === "tinted"
      ? `color-mix(in srgb, ${toneColor(theme, $tone)} 12%, ${theme.color.bg.surfaceRaised})`
      : theme.color.bg.surfaceRaised};

  /* dangerSurface 카드는 시트 알림 카드처럼 균일 1px 외곽 — 색 띠 없음. 조건을 슬롯마다
     되묻지 않고 한 번만 판정해 덮어쓴다(border-color 가 left 색까지 함께 덮는다). */
  ${({ theme, $tone, $variant }) =>
    isDangerSurface($tone, $variant) &&
    css`
      border-color: ${theme.color.border.dangerSurface};
      border-left-width: 1px;
      background-color: ${theme.color.bg.dangerSurface};
    `}
  box-shadow: ${({ theme }) => theme.elevation.shadow.modal};
  pointer-events: auto;
  /* replace로 tone이 바뀔 때 띠·면 색이 순간 스왑 대신 짧게 넘어간다. */
  ${controlTransition("border-color", "background-color")}
  ${({ $motion }) => {
    const preset = MOTION[$motion];
    return css`
      animation: ${preset.enter} ${preset.enterMs}ms ${preset.enterEasing};

      &[data-exiting="true"] {
        animation: ${preset.exit} ${preset.exitMs}ms ${preset.exitEasing} forwards;
        pointer-events: none;
      }
    `;
  }}

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    &[data-exiting="true"] {
      animation: none;
    }
  }
`;

const IconSlot = styled.span<{ $tone: ToastTone; $swap?: boolean }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  /* 20px — icon variant에서는 아이콘이 tone을 전달하므로 본문(14px)보다 확연히 크게 둔다. */
  font-size: ${({ theme }) => theme.typography.size.head};
  /* 카드 gap 8px에 2px를 더해 아이콘–문구 간격만 10px로 벌린다. */
  margin-inline-end: 0.125rem;
  color: ${({ theme, $tone }) => toneColor(theme, $tone)};
  ${({ $swap }) => ($swap ? contentSwapCss : "")}
`;

const Message = styled(Text)<{ $onDangerSurface?: boolean; $swap?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  ${({ $swap }) => ($swap ? contentSwapCss : "")}
  ${({ $onDangerSurface, theme }) =>
    $onDangerSurface
      ? css`
          color: ${theme.color.text.onDangerSurface};
        `
      : ""}
`;

const CloseButton = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.15rem;
  border: 0;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: none;
  color: ${({ theme }) => theme.color.text.muted};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.size.title};
  line-height: 1;
  ${controlTransition("color")}

  &:hover {
    color: ${({ theme }) => theme.color.text.default};
  }

  &:focus-visible {
    ${controlFocusRing()}
  }
`;

// tone별 leading 아이콘. 채움형 c6 UI glyph — 알림에서는 아이콘이 tone을 전달하므로 선보다 색 면적이 큰
// 채움을 쓴다. 면 색은 IconSlot이 currentColor로 입히고 기호는 글리프가 `text.onStatus`로 얹는다.
const TONE_ICON: Record<ToastTone, (props: IconProps) => ReactNode> = {
  info: InfoFilledIcon,
  success: CheckCircleFilledIcon,
  warning: WarningFilledIcon,
  error: CloseCircleFilledIcon,
};

// 카드 식별 키. id만으로는 "같은 자리 교체"와 "닫힌 뒤 다시 띄움"을 가를 수 없다(한 이벤트에서 닫고 다시 띄우면
// 스토어 통지가 묶여 사라진 순간을 못 본다). instance가 다르면 다른 카드다.
const presenceKey = (toast: Toast): string => `${toast.id}#${toast.instance}`;

/**
 * 스토어에서 사라진 토스트를 exit 애니메이션 동안 잠깐 더 mount해 두는 presence 버퍼.
 * exit 중인 항목은 사라진 시점의 프리셋(`exitMotion`)을 고정해 이후 `motion`이 바뀌어도 애니메이션과 제거
 * 시점이 어긋나지 않는다(같은 keyframe을 쓰는 프리셋끼리는 브라우저가 애니메이션을 재시작하지 않는다).
 * prefers-reduced-motion이면 버퍼 없이 스토어 그대로 반영(즉시 제거).
 */
type PresenceItem = { toast: Toast; exitMotion: ToastMotion | null };

function useToastPresence(toasts: readonly Toast[], motion: ToastMotion): readonly PresenceItem[] {
  const [items, setItems] = useState<readonly PresenceItem[]>(() =>
    toasts.map((toast) => ({ toast, exitMotion: null })),
  );
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    if (prefersReducedMotion()) {
      for (const timer of timersRef.current.values()) {
        clearTimeout(timer);
      }
      timersRef.current.clear();
      setItems(toasts.map((toast) => ({ toast, exitMotion: null })));
      return;
    }

    const storeByKey = new Map(toasts.map((toast) => [presenceKey(toast), toast]));
    setItems((prev) => {
      const next: PresenceItem[] = [];
      const seen = new Set<string>();
      for (const item of prev) {
        const key = presenceKey(item.toast);
        const fresh = storeByKey.get(key);
        if (fresh) {
          next.push({ toast: fresh, exitMotion: null });
        } else {
          // 스토어에서 사라짐 → 지금 프리셋으로 exit 애니메이션 후 제거 예약(중복 예약 방지).
          const exitMotion = item.exitMotion ?? motion;
          next.push({ toast: item.toast, exitMotion });
          if (!timersRef.current.has(key)) {
            const timer = setTimeout(() => {
              timersRef.current.delete(key);
              setItems((current) => current.filter((entry) => presenceKey(entry.toast) !== key));
            }, MOTION[exitMotion].exitMs);
            timersRef.current.set(key, timer);
          }
        }
        seen.add(key);
      }
      // 새로 추가된 토스트는 기존 위치를 보존한 채 끝에 append(닫힌 뒤 다시 뜬 같은 id도 새 instance라 여기로).
      for (const toast of toasts) {
        if (!seen.has(presenceKey(toast))) {
          next.push({ toast, exitMotion: null });
        }
      }
      return next;
    });
  }, [toasts, motion]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
    };
  }, []);

  return items;
}

/**
 * 전역 토스트 호스트. 어느 화면에서도 일시 알림을 스택으로 보여준다. 큐·자동 닫힘은
 * 주입된(기본 싱글턴) 매니저가 소유하므로 컴포넌트 밖에서도 `toast.success(...)`로 띄울 수 있다.
 *
 * `document.body`로 portal해 transform/overflow 조상의 좌표 영향을 받지 않고, `overlayZIndex.toast`
 * (모달 위)에 떠서 모달 `inert`에도 잡히지 않는다.
 */
export function ToastHost({
  manager = defaultManager,
  gravity = "top-right",
  variant = "stripe",
  motion = "subtle",
  pauseOnHover = true,
}: ToastHostProps) {
  const toasts = useSyncExternalStore(manager.subscribe, manager.getSnapshot, manager.getSnapshot);
  const items = useToastPresence(toasts, motion);
  // SSR/hydration 안전: 마운트 후에만 portal한다(서버엔 document가 없다).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // hover/focus 일시정지 상태. 포인터와 키보드 포커스가 모두 빠졌을 때만 자동 닫힘을 재개한다.
  const interactingRef = useRef({ hover: false, focus: false });
  const syncPause = () => {
    if (!pauseOnHover) {
      return;
    }
    const { hover, focus } = interactingRef.current;
    if (hover || focus) {
      manager.pause();
    } else {
      manager.resume();
    }
  };

  const empty = toasts.length === 0;
  // 스택이 비면(마지막 토스트가 hover 중 닫혀 mouseleave 가 안 뜨는 경우 포함) 멈춤 상태가 매니저에 남지
  // 않도록 재개한다. 새 토스트가 다시 뜨면 그때의 hover/focus 로 다시 멈춘다.
  useEffect(() => {
    if (empty && pauseOnHover) {
      interactingRef.current = { hover: false, focus: false };
      manager.resume();
    }
  }, [empty, pauseOnHover, manager]);

  // 호스트가 사라지거나 pauseOnHover 가 꺼질 때 멈춤 상태를 정리한다(매니저는 싱글턴이라 잔류 위험).
  useEffect(() => {
    if (!pauseOnHover) {
      return;
    }
    return () => {
      interactingRef.current = { hover: false, focus: false };
      manager.resume();
    };
  }, [manager, pauseOnHover]);

  const handleHoverStart = () => {
    interactingRef.current.hover = true;
    syncPause();
  };
  const handleHoverEnd = () => {
    interactingRef.current.hover = false;
    syncPause();
  };
  const handleFocusStart = () => {
    interactingRef.current.focus = true;
    syncPause();
  };
  const handleFocusEnd = (event: FocusEvent<HTMLDivElement>) => {
    // 포커스가 호스트 내부의 다른 요소로 이동하는 경우는 '빠짐'으로 보지 않는다(재개 깜빡임 방지).
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }
    interactingRef.current.focus = false;
    syncPause();
  };

  if (!mounted || items.length === 0) {
    return null;
  }

  const showIcon = variant === "icon" || variant === "icon-stripe";

  return createPortal(
    <Host
      $gravity={gravity}
      data-c6-toast-root
      role="region"
      aria-label="Notifications"
      onMouseEnter={pauseOnHover ? handleHoverStart : undefined}
      onMouseLeave={pauseOnHover ? handleHoverEnd : undefined}
      onFocus={pauseOnHover ? handleFocusStart : undefined}
      onBlur={pauseOnHover ? handleFocusEnd : undefined}
    >
      {items.map(({ toast, exitMotion }) => {
        // 실패·경고는 assertive(role=alert), 안내·성공은 polite(role=status)로 알린다.
        const assertive = toast.tone === "error" || toast.tone === "warning";
        const ToneIcon = TONE_ICON[toast.tone];
        // exit 중인 카드는 사라진 시점의 프리셋을 끝까지 쓴다.
        const cardMotion = exitMotion ?? motion;
        // 처음 뜬 카드(revision 0)는 enter만, replace된 카드는 내용 교체 큐를 탄다.
        const updated = toast.revision > 0;
        const { tx, ty, dx, dy } = MOTION[cardMotion].offset?.[gravity] ?? NO_OFFSET;
        const slideVars = {
          "--c6-toast-tx": tx,
          "--c6-toast-ty": ty,
          "--c6-toast-dx": String(dx),
          "--c6-toast-dy": String(dy),
        } as CSSProperties;
        return (
          <Card
            // 같은 instance의 교체는 tone이 polite↔assertive를 넘어도 노드를 유지한다 — 카드 안 포커스와
            // enter 모션 생략이 격상 알림의 끼어들기 보장보다 우선이다. role/aria-live는 제자리에서 바뀐다.
            key={presenceKey(toast)}
            $tone={toast.tone}
            $variant={variant}
            $motion={cardMotion}
            data-tone={toast.tone}
            data-revision={toast.revision}
            data-motion={cardMotion}
            data-exiting={exitMotion !== null}
            style={slideVars}
            role={assertive ? "alert" : "status"}
            aria-live={assertive ? "assertive" : "polite"}
          >
            {updated ? (
              // key로 replace마다 훑기만 재시작한다(카드 노드는 유지).
              <Sheen
                key={`sheen-${toast.revision}`}
                $tone={toast.tone}
                data-c6-toast-update
                aria-hidden="true"
              />
            ) : null}
            {showIcon ? (
              <IconSlot
                key={`icon-${toast.revision}`}
                $tone={toast.tone}
                $swap={updated}
                data-c6-toast-icon
                aria-hidden="true"
              >
                <ToneIcon />
              </IconSlot>
            ) : null}
            <Message
              key={`message-${toast.revision}`}
              variant={defaultTextVariants["body-normal"]}
              $onDangerSurface={isDangerSurface(toast.tone, variant)}
              $swap={updated}
            >
              {toast.message}
            </Message>
            {toast.action ? (
              <IconButton
                text={toast.action.label}
                size="sm"
                variant="surface"
                onClick={() => {
                  const action = toast.action;
                  if (!action) {
                    return;
                  }
                  action.onPress();
                  // 머무는 액션은 onPress가 같은 id로 replace한 내용을 지우지 않도록 닫지 않는다.
                  if (action.dismissOnPress !== false) {
                    manager.dismiss(toast.id);
                  }
                }}
              />
            ) : null}
            <CloseButton
              type="button"
              aria-label="Dismiss notification"
              onClick={() => manager.dismiss(toast.id)}
            >
              <CloseIcon />
            </CloseButton>
          </Card>
        );
      })}
    </Host>,
    document.body,
  );
}

ToastHost.displayName = "ToastHost";
